import React, { useId, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { DayPicker, getDefaultClassNames } from 'react-day-picker';
import type { ClassNames } from 'react-day-picker';
import * as Popover from '@radix-ui/react-popover';
import dayjs from 'dayjs';
import AppIcon from './AppIcon';
import { cn } from '../utils/cn';
import { getDateFormat, getTimeFormat, getDateTimeFormat } from '../utils/dateFormatter';
import { formatLocalDateParam } from '../utils/apiDate';
import { TimeField } from './dateFields';

/* ─── Tailwind classNames for react-day-picker v9 ─── */
const defaultClassNames = getDefaultClassNames();

const dayPickerClassNames: Partial<ClassNames> = {
  root: `${defaultClassNames.root} p-3`,
  months: `${defaultClassNames.months} relative flex flex-col sm:flex-row gap-4`,
  month: `${defaultClassNames.month} space-y-4`,
  // Header row: dropdowns hug the start, the nav pager is absolutely docked to the end.
  month_caption: `${defaultClassNames.month_caption} flex h-9 items-center justify-start`,
  caption_label: `${defaultClassNames.caption_label} flex items-center gap-1 px-2 py-1 text-sm font-medium text-foreground`,
  dropdowns: `${defaultClassNames.dropdowns} flex items-center justify-start gap-1.5`,
  dropdown_root: `${defaultClassNames.dropdown_root} relative inline-flex items-center rounded-md border border-input bg-background hover:bg-muted transition-colors focus-within:ring-2 focus-within:ring-primary/20 focus-within:border-primary`,
  dropdown: `${defaultClassNames.dropdown} absolute inset-0 z-10 w-full cursor-pointer appearance-none opacity-0`,
  months_dropdown: `${defaultClassNames.months_dropdown}`,
  years_dropdown: `${defaultClassNames.years_dropdown}`,
  // Two arrows grouped as a single pager control, pinned to the top-end corner (RTL-safe via end-0).
  nav: `${defaultClassNames.nav} absolute top-0 end-0 z-10 flex h-9 items-center gap-0.5`,
  button_previous: `${defaultClassNames.button_previous} h-7 w-7 bg-transparent p-0 text-muted-foreground hover:text-foreground rounded-md hover:bg-muted transition-colors inline-flex items-center justify-center disabled:opacity-40 disabled:pointer-events-none`,
  button_next: `${defaultClassNames.button_next} h-7 w-7 bg-transparent p-0 text-muted-foreground hover:text-foreground rounded-md hover:bg-muted transition-colors inline-flex items-center justify-center disabled:opacity-40 disabled:pointer-events-none`,
  month_grid: `${defaultClassNames.month_grid} w-full border-collapse`,
  weekdays: `${defaultClassNames.weekdays} flex`,
  weekday: `${defaultClassNames.weekday} text-muted-foreground rounded-md w-9 font-normal text-xs`,
  week: `${defaultClassNames.week} flex w-full mt-2`,
  day: `${defaultClassNames.day} h-9 w-9 text-center text-sm p-0 relative`,
  day_button: `${defaultClassNames.day_button} h-9 w-9 p-0 font-normal rounded-md hover:bg-muted transition-colors`,
  selected: `${defaultClassNames.selected} bg-primary text-primary-foreground hover:bg-primary`,
  today: `${defaultClassNames.today} bg-accent text-accent-foreground`,
  outside: `${defaultClassNames.outside} text-muted-foreground opacity-50`,
  disabled: `${defaultClassNames.disabled} text-muted-foreground opacity-50 cursor-not-allowed`,
};

/* ─── Shared helper: year-dropdown bounds ───
   react-day-picker's dropdown caption needs an explicit start/end month to
   build the year list. Fall back to a wide range so birth dates, hire dates,
   expiry dates, etc. are all reachable without endless month-arrow clicking. */
function getDropdownBounds(minDate?: Date, maxDate?: Date): { startMonth: Date; endMonth: Date } {
  const now = new Date();
  const startMonth = minDate ?? new Date(now.getFullYear() - 100, 0, 1);
  const endMonth = maxDate ?? new Date(now.getFullYear() + 10, 11, 31);
  return { startMonth, endMonth };
}

/* ─── Shared helper: resolve deprecated props ─── */
function resolveDeprecatedProps({
  fullWidth,
  size,
  sx,
  className,
  variant,
}: {
  fullWidth?: boolean;
  size?: 'small' | 'medium';
  sx?: React.CSSProperties;
  className?: string;
  variant?: 'default' | 'compact';
}): { className: string; variant: 'default' | 'compact' } {
  const classes: string[] = [];

  // Default is full width
  if (fullWidth !== false) {
    classes.push('w-full');
  }

  // Map sx width to className
  if (sx && typeof sx === 'object' && 'width' in sx) {
    const w = sx.width;
    if (typeof w === 'number') {
      classes.push(`w-[${w}px]`);
      // Remove default w-full if explicit width given
      const idx = classes.indexOf('w-full');
      if (idx >= 0) classes.splice(idx, 1);
    }
  }

  if (className) classes.push(className);

  // Resolve variant
  let resolvedVariant = variant || 'default';
  if (!variant && size === 'small') {
    resolvedVariant = 'compact';
  }

  return { className: classes.join(' '), variant: resolvedVariant };
}

/* ─── Shared input field ─── */
function PickerInput({
  label,
  displayValue,
  required,
  disabled,
  error,
  helperText,
  isCompact,
  className,
  iconName,
  onClick,
  inputRef,
  ariaLabel,
}: {
  label: string;
  displayValue: string;
  required?: boolean;
  disabled?: boolean;
  error?: boolean;
  helperText?: string;
  isCompact: boolean;
  className?: string;
  iconName: 'Calendar' | 'Clock' | 'CalendarClock';
  onClick?: () => void;
  inputRef?: React.Ref<HTMLButtonElement>;
  ariaLabel?: string;
}) {
  const inputId = useId();
  // Derived from the same stable useId() used for the button's own id, so the
  // helper text has a stable id to be referenced by aria-describedby without
  // requiring callers to pass an explicit id/name prop.
  const helperId = `${inputId}-helper-text`;
  return (
    <div className={cn('space-y-1', className)}>
      {label && (
        <label htmlFor={inputId} className={cn('text-sm font-medium text-foreground', error && 'text-destructive')}>
          {label}
          {required && <span className="text-destructive ms-1">*</span>}
        </label>
      )}
      <button
        id={inputId}
        ref={inputRef}
        type="button"
        onClick={onClick}
        disabled={disabled}
        aria-label={ariaLabel || label || undefined}
        aria-invalid={error || undefined}
        aria-describedby={helperText ? helperId : undefined}
        className={cn(
          'flex items-center w-full rounded-md border border-input bg-background text-sm text-start',
          'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/20 focus-visible:border-primary',
          'disabled:cursor-not-allowed disabled:opacity-50',
          error && 'border-destructive focus-visible:ring-destructive/50',
          isCompact ? 'h-8 px-2 py-1 text-xs gap-1.5' : 'h-10 px-3 py-2 gap-2'
        )}
      >
        <AppIcon name={iconName} size={isCompact ? 14 : 16} className="text-muted-foreground shrink-0" />
        <span className={cn('flex-1 truncate', !displayValue && 'text-muted-foreground')}>
          {displayValue || label}
        </span>
      </button>
      {helperText && (
        <p id={helperId} className={cn('text-xs', error ? 'text-destructive' : 'text-muted-foreground')}>
          {helperText}
        </p>
      )}
    </div>
  );
}

/* ═══════════════════════════════════════════
   DatePicker
   ═══════════════════════════════════════════ */

type DatePickerProps = {
  label: string;
  value: Date | string | null;
  onChange: (date: Date | null) => void;
  format?: string;
  required?: boolean;
  disabled?: boolean;
  error?: boolean;
  helperText?: string;
  minDate?: Date;
  maxDate?: Date;
  className?: string;
  variant?: 'default' | 'compact';
  /** Accessible name for the underlying button. Falls back to `label` when omitted,
   *  so this is opt-in and backward-compatible with every existing call site. Use
   *  this when the visible `label` is empty/omitted but the field still needs an
   *  accessible name (e.g. when a sibling element already renders the visible label). */
  ariaLabel?: string;
  /** @deprecated Use className="w-full" instead */
  fullWidth?: boolean;
  /** @deprecated Use variant="compact" instead */
  size?: 'small' | 'medium';
  /** @deprecated Use className instead */
  sx?: React.CSSProperties;
};

export const DatePicker: React.FC<DatePickerProps> = ({
  label,
  value,
  onChange,
  format,
  required = false,
  disabled = false,
  error,
  helperText,
  minDate,
  maxDate,
  className: classNameProp,
  variant: variantProp,
  ariaLabel,
  fullWidth = true,
  size = 'small',
  sx,
}) => {
  const [open, setOpen] = useState(false);
  const { className, variant } = resolveDeprecatedProps({ fullWidth, size, sx, className: classNameProp, variant: variantProp });
  const isCompact = variant === 'compact';

  const dateFormat = format || getDateFormat('display');
  const dateValue = value ? dayjs(value).toDate() : undefined;
  const displayValue = value ? dayjs(value).format(dateFormat) : '';

  const handleSelect = (day: Date | undefined) => {
    if (day) {
      onChange(day);
      setOpen(false);
    } else {
      onChange(null);
    }
  };

  return (
    <Popover.Root open={open} onOpenChange={(v) => { if (!disabled) setOpen(v); }}>
      <Popover.Trigger asChild disabled={disabled}>
        <div>
          <PickerInput
            label={label}
            displayValue={displayValue}
            required={required}
            disabled={disabled}
            error={error}
            helperText={helperText}
            isCompact={isCompact}
            className={className}
            iconName="Calendar"
            ariaLabel={ariaLabel}
          />
        </div>
      </Popover.Trigger>
      <Popover.Portal>
        <Popover.Content
          className="z-50 rounded-md border border-border bg-card shadow-md animate-in fade-in-0 zoom-in-95"
          align="start"
          sideOffset={4}
        >
          <DayPicker
            mode="single"
            captionLayout="dropdown"
            fixedWeeks
            showOutsideDays
            selected={dateValue}
            onSelect={handleSelect}
            defaultMonth={dateValue}
            startMonth={getDropdownBounds(minDate, maxDate).startMonth}
            endMonth={getDropdownBounds(minDate, maxDate).endMonth}
            disabled={minDate || maxDate ? [
              ...(minDate ? [{ before: minDate }] : []),
              ...(maxDate ? [{ after: maxDate }] : []),
            ] : undefined}
            classNames={dayPickerClassNames}
          />
        </Popover.Content>
      </Popover.Portal>
    </Popover.Root>
  );
};

/* ═══════════════════════════════════════════
   TimePicker
   ═══════════════════════════════════════════ */

type TimePickerProps = {
  label: string;
  value: Date | string | null;
  onChange: (date: Date | null) => void;
  format?: string;
  required?: boolean;
  disabled?: boolean;
  error?: boolean;
  helperText?: string;
  className?: string;
  variant?: 'default' | 'compact';
  /** @deprecated Use className="w-full" instead */
  fullWidth?: boolean;
  /** @deprecated Use variant="compact" instead */
  size?: 'small' | 'medium';
  /** @deprecated Use className instead */
  sx?: React.CSSProperties;
};

export const TimePicker: React.FC<TimePickerProps> = ({
  label,
  value,
  onChange,
  format,
  required = false,
  disabled = false,
  error,
  helperText,
  className: classNameProp,
  variant: variantProp,
  fullWidth = true,
  size = 'small',
  sx,
}) => {
  const { className, variant } = resolveDeprecatedProps({ fullWidth, size, sx, className: classNameProp, variant: variantProp });
  const isCompact = variant === 'compact';
  // Native <input type="time"> renders 12-hour AM/PM on some machines — the system is 24-hour
  // HH:mm everywhere (owner decision 2026-09-17), so this delegates to the typed TimeField.
  const timeString = value ? dayjs(value).format('HH:mm') : '';

  const handleChange = (timeVal: string) => {
    if (!timeVal) {
      onChange(null);
      return;
    }
    const [hours, minutes] = timeVal.split(':').map(Number);
    // Preserve the date part from the existing value, or use today
    const base = value ? dayjs(value) : dayjs();
    onChange(base.hour(hours).minute(minutes).second(0).millisecond(0).toDate());
  };

  return (
    <TimeField
      label={label}
      value={timeString}
      onChange={handleChange}
      required={required}
      disabled={disabled}
      error={error ? (helperText || true) : undefined}
      description={error ? undefined : helperText}
      className={className}
      inputClassName={isCompact ? 'h-8 py-1 text-xs' : undefined}
    />
  );
};

/* ═══════════════════════════════════════════
   DateTimePicker
   ═══════════════════════════════════════════ */

type DateTimePickerProps = {
  label: string;
  value: Date | string | null;
  onChange: (date: Date | null) => void;
  format?: string;
  required?: boolean;
  disabled?: boolean;
  error?: boolean;
  helperText?: string;
  minDate?: Date;
  maxDate?: Date;
  className?: string;
  variant?: 'default' | 'compact';
  /** @deprecated Use className="w-full" instead */
  fullWidth?: boolean;
  /** @deprecated Use variant="compact" instead */
  size?: 'small' | 'medium';
  /** @deprecated Use className instead */
  sx?: React.CSSProperties;
};

export const DateTimePicker: React.FC<DateTimePickerProps> = ({
  label,
  value,
  onChange,
  format,
  required = false,
  disabled = false,
  error,
  helperText,
  minDate,
  maxDate,
  className: classNameProp,
  variant: variantProp,
  fullWidth = true,
  size = 'small',
  sx,
}) => {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const { className, variant } = resolveDeprecatedProps({ fullWidth, size, sx, className: classNameProp, variant: variantProp });
  const isCompact = variant === 'compact';

  const dateTimeFormat = format || getDateTimeFormat('display');
  const dateValue = value ? dayjs(value).toDate() : undefined;
  const displayValue = value ? dayjs(value).format(dateTimeFormat) : '';

  // Track time separately so user can adjust after picking a date
  const timeString = value ? dayjs(value).format('HH:mm') : '';

  const handleDateSelect = (day: Date | undefined) => {
    if (!day) {
      onChange(null);
      return;
    }
    // Preserve existing time when changing date
    const existingTime = value ? dayjs(value) : dayjs();
    const newDate = dayjs(day)
      .hour(existingTime.hour())
      .minute(existingTime.minute())
      .second(0)
      .millisecond(0)
      .toDate();
    onChange(newDate);
    // Don't close — user may want to adjust time
  };

  const handleTimeChange = (timeVal: string) => {
    if (!timeVal) return;
    const [hours, minutes] = timeVal.split(':').map(Number);
    const base = value ? dayjs(value) : dayjs();
    const newDate = base.hour(hours).minute(minutes).second(0).millisecond(0).toDate();
    onChange(newDate);
  };

  return (
    <Popover.Root open={open} onOpenChange={(v) => { if (!disabled) setOpen(v); }}>
      <Popover.Trigger asChild disabled={disabled}>
        <div>
          <PickerInput
            label={label}
            displayValue={displayValue}
            required={required}
            disabled={disabled}
            error={error}
            helperText={helperText}
            isCompact={isCompact}
            className={className}
            iconName="CalendarClock"
          />
        </div>
      </Popover.Trigger>
      <Popover.Portal>
        <Popover.Content
          className="z-50 rounded-md border border-border bg-card shadow-md animate-in fade-in-0 zoom-in-95"
          align="start"
          sideOffset={4}
        >
          <DayPicker
            mode="single"
            captionLayout="dropdown"
            fixedWeeks
            showOutsideDays
            selected={dateValue}
            onSelect={handleDateSelect}
            defaultMonth={dateValue}
            startMonth={getDropdownBounds(minDate, maxDate).startMonth}
            endMonth={getDropdownBounds(minDate, maxDate).endMonth}
            disabled={minDate || maxDate ? [
              ...(minDate ? [{ before: minDate }] : []),
              ...(maxDate ? [{ after: maxDate }] : []),
            ] : undefined}
            classNames={dayPickerClassNames}
          />
          <div className="border-t border-border px-3 py-2 flex items-center gap-2">
            <AppIcon name="Clock" size={14} className="text-muted-foreground" />
            <TimeField
              value={timeString}
              onChange={handleTimeChange}
              aria-label={t('Time')}
              className="flex-1"
              inputClassName="h-8 py-1"
            />
            <button
              type="button"
              onClick={() => setOpen(false)}
              className="rounded bg-primary px-3 py-1 text-xs font-medium text-primary-foreground hover:bg-primary/90 transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
            >
              {t('Done')}
            </button>
          </div>
        </Popover.Content>
      </Popover.Portal>
    </Popover.Root>
  );
};

/* ─── Utility export (unchanged) ─── */
export const formatDateForInput = (date?: Date): string => {
  return date ? formatLocalDateParam(date) : '';
};
